/**
 * Utility function for formatting API errors into user-friendly messages.
 *
 * This provides consistent error messaging across the application,
 * translating technical errors into actionable user guidance.
 */

import {
  isSeatLimitError,
  isDuplicateEmailError,
  isAlreadyExistsError,
  isCannotModifyVendorError,
  isCannotModifyProtectedError,
  isApiError,
} from '../types/errors';
import { isHttpError } from '../api/http';

/**
 * User-friendly error messages for specific error types.
 */
const ERROR_MESSAGES = {
  SEAT_LIMIT: 'Seat limit reached — contact ModernPath',
  DUPLICATE_EMAIL: 'User already exists in this organization',
  ALREADY_EXISTS: 'A tenant with this name or domain already exists.',
  CANNOT_MODIFY_VENDOR: 'The ModernPath vendor organization is protected and cannot be deleted.',
  CANNOT_MODIFY_PROTECTED: 'This organization is protected and cannot be deleted.',
  GENERIC: 'Something went wrong. Please try again.',
} as const;

/**
 * Formats an API error into a user-friendly message.
 *
 * This function handles various error shapes and provides appropriate
 * messages for known error types while falling back to a generic message
 * for unknown errors.
 *
 * Known error types:
 * - Seat limit exceeded (403 with SEAT_LIMIT_EXCEEDED code) → specific message
 * - Duplicate email (409 with DUPLICATE_EMAIL code) → specific message
 * - Tenant name/domain collision (409 with ALREADY_EXISTS code) → specific message
 * - Vendor org (409 with CANNOT_MODIFY_VENDOR code) → vendor-specific message
 * - Other protected system org (409 with CANNOT_MODIFY_PROTECTED code) → protected message
 * - Other errors → generic message
 *
 * The full error is always logged to the console for debugging purposes.
 *
 * @param error - The error object (can be various shapes: Error, ApiError, HttpError, unknown)
 * @returns A user-friendly error message string
 *
 * @example
 * ```ts
 * // Seat limit error
 * formatErrorMessage({ status: 403, code: 'SEAT_LIMIT_EXCEEDED', message: 'Limit reached' })
 * // Returns: 'Seat limit reached — contact ModernPath'
 *
 * // Duplicate email error
 * formatErrorMessage({ status: 409, code: 'DUPLICATE_EMAIL', message: 'Email exists' })
 * // Returns: 'User already exists in this organization'
 *
 * // Unknown error
 * formatErrorMessage(new Error('Network failed'))
 * // Returns: 'Something went wrong. Please try again.'
 * ```
 */
export function formatErrorMessage(error: unknown): string {
  // Always log the full error for debugging
  console.error('API Error:', error);

  // Check if it's an HttpError and convert to ApiError for type checking
  if (isHttpError(error)) {
    const apiError = error.toApiError();

    if (isSeatLimitError(apiError)) {
      return ERROR_MESSAGES.SEAT_LIMIT;
    }

    if (isDuplicateEmailError(apiError)) {
      return ERROR_MESSAGES.DUPLICATE_EMAIL;
    }

    // Tenant lifecycle 409s, keyed on code so they take precedence over the
    // brittle status+"exist" fallback below (which would otherwise mislabel an
    // org name/domain collision as a duplicate *user email*).
    if (isAlreadyExistsError(apiError)) {
      return ERROR_MESSAGES.ALREADY_EXISTS;
    }
    if (isCannotModifyVendorError(apiError)) {
      return ERROR_MESSAGES.CANNOT_MODIFY_VENDOR;
    }
    if (isCannotModifyProtectedError(apiError)) {
      return ERROR_MESSAGES.CANNOT_MODIFY_PROTECTED;
    }

    // Check for seat limit by status code as a fallback
    // Some backends might return 403 for seat limits without the specific code
    if (apiError.status === 403 && apiError.message?.toLowerCase().includes('seat')) {
      return ERROR_MESSAGES.SEAT_LIMIT;
    }

    // Check for duplicate by status code as a fallback
    if (apiError.status === 409 && apiError.message?.toLowerCase().includes('exist')) {
      return ERROR_MESSAGES.DUPLICATE_EMAIL;
    }
  }

  // Handle ApiError directly (might be passed from error boundaries, etc.)
  if (isApiError(error)) {
    if (isSeatLimitError(error)) {
      return ERROR_MESSAGES.SEAT_LIMIT;
    }

    if (isDuplicateEmailError(error)) {
      return ERROR_MESSAGES.DUPLICATE_EMAIL;
    }

    // Tenant lifecycle 409s, keyed on code (see the HttpError branch above).
    if (isAlreadyExistsError(error)) {
      return ERROR_MESSAGES.ALREADY_EXISTS;
    }
    if (isCannotModifyVendorError(error)) {
      return ERROR_MESSAGES.CANNOT_MODIFY_VENDOR;
    }
    if (isCannotModifyProtectedError(error)) {
      return ERROR_MESSAGES.CANNOT_MODIFY_PROTECTED;
    }

    // Fallback checks for API errors without specific codes
    if (error.status === 403 && error.message?.toLowerCase().includes('seat')) {
      return ERROR_MESSAGES.SEAT_LIMIT;
    }

    if (error.status === 409 && error.message?.toLowerCase().includes('exist')) {
      return ERROR_MESSAGES.DUPLICATE_EMAIL;
    }
  }

  // Return generic message for all other error types
  return ERROR_MESSAGES.GENERIC;
}

/**
 * Exported error messages for testing purposes.
 * @internal
 */
export const _ERROR_MESSAGES = ERROR_MESSAGES;
